/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Polygon, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Input, Modal } from "antd";
import { EyeOutlined, SearchOutlined } from "@ant-design/icons";

import ContentTop from "../../components/ContentTop";
import Tabs from "../../components/Tabs";

import { sttGpsDevices } from "./fakeDevices";
import "./SttGps.css";

const PAGE_SIZE = 10;
const MAP_CENTER = { lat: 41.3775, lng: 64.5853 };
const MAP_OUTER_BOUNDS = [
  { lat: 85, lng: -180 },
  { lat: 85, lng: 180 },
  { lat: -85, lng: 180 },
  { lat: -85, lng: -180 },
];
const UZBEKISTAN_BOUNDARY = [
  { lat: 41.30864166926936, lng: 55.96819135928291 },
  { lat: 44.99585846615911, lng: 55.9289172707411 },
  { lat: 45.586804307632974, lng: 58.50312706892845 },
  { lat: 45.50001373959873, lng: 58.68998904809581 },
  { lat: 44.78403677019473, lng: 60.23997195825834 },
  { lat: 44.40581696225051, lng: 61.05831994003245 },
  { lat: 43.50447663021565, lng: 62.01330040878625 },
  { lat: 43.650074978198006, lng: 63.18578698105657 },
  { lat: 43.72808055274258, lng: 64.90082441595928 },
  { lat: 42.997660020513095, lng: 66.09801232286509 },
  { lat: 41.99464630794404, lng: 66.02339155463562 },
  { lat: 41.987644151368556, lng: 66.51064863471572 },
  { lat: 41.1684435084615, lng: 66.71404707221652 },
  { lat: 41.13599070898222, lng: 67.98585574735182 },
  { lat: 40.6623245305949, lng: 68.25989586779562 },
  { lat: 40.66868073176681, lng: 68.63248294462002 },
  { lat: 41.38424428971234, lng: 69.07002729683524 },
  { lat: 42.08130768489745, lng: 70.3889648782208 },
  { lat: 42.266154283205495, lng: 70.96231489449914 },
  { lat: 42.16771067968946, lng: 71.25924767444823 },
  { lat: 41.51999827734314, lng: 70.42002241402821 },
  { lat: 41.14358714452912, lng: 71.1578585142916 },
  { lat: 41.392900092121266, lng: 71.87011478057048 },
  { lat: 40.866033026689465, lng: 73.05541710804917 },
  { lat: 40.14584442805378, lng: 71.77487511585656 },
  { lat: 40.24436554621823, lng: 71.01419803252017 },
  { lat: 40.21852733007229, lng: 70.60140669137269 },
  { lat: 40.49649485937029, lng: 70.45815962105962 },
  { lat: 40.960213324541414, lng: 70.66662234892505 },
  { lat: 40.72782440852485, lng: 69.32949466337283 },
  { lat: 40.08615814875667, lng: 69.01163292834551 },
  { lat: 39.53345286717894, lng: 68.53641645698943 },
  { lat: 39.58047842056453, lng: 67.70142866401736 },
  { lat: 39.140143541005486, lng: 67.44221967964131 },
  { lat: 38.901553453113905, lng: 68.17602501818593 },
  { lat: 38.15702525486874, lng: 68.39203250516596 },
  { lat: 37.144994004864685, lng: 67.82999962755952 },
  { lat: 37.35614390720929, lng: 67.07578209825962 },
  { lat: 37.36278432875879, lng: 66.51860680528867 },
  { lat: 37.97468496352687, lng: 66.54615034370022 },
  { lat: 38.4026950139843, lng: 65.21599897650739 },
  { lat: 38.892406724598246, lng: 64.17022301621677 },
  { lat: 39.36325653742564, lng: 63.51801476426103 },
  { lat: 40.05388621679039, lng: 62.374260288345006 },
  { lat: 41.084856879229406, lng: 61.88271406438469 },
  { lat: 41.266370347654615, lng: 61.54717898951356 },
  { lat: 41.22032664648255, lng: 60.465952996670694 },
  { lat: 41.425146185871405, lng: 60.083340691981675 },
  { lat: 42.22308197689021, lng: 59.976422153569786 },
  { lat: 42.75155101172305, lng: 58.62901085799146 },
  { lat: 42.17055288346552, lng: 57.78652998233708 },
  { lat: 41.826026109375604, lng: 56.932215203687804 },
  { lat: 41.32231008561057, lng: 57.0963912290791 },
];
const pathArea = (path) =>
  path.reduce((area, point, index) => {
    const next = path[(index + 1) % path.length];
    return area + point.lng * next.lat - next.lng * point.lat;
  }, 0) / 2;

const isClockwise = (path) => pathArea(path) < 0;
const clockwisePath = (path) => (isClockwise(path) ? path : [...path].reverse());
const counterClockwisePath = (path) =>
  isClockwise(path) ? [...path].reverse() : path;
const UZBEKISTAN_MASK_PATHS = [
  clockwisePath(MAP_OUTER_BOUNDS),
  counterClockwisePath(UZBEKISTAN_BOUNDARY),
];

const tabs = [
  { title: "Hisobot", id: 1, type: "text" },
  { title: "Xarita", id: 2, type: "text" },
];

const getStatusClass = (status) => (status === "Faol" ? "active" : "inactive");

const uniqueValues = (items, key) =>
  Array.from(new Set(items.map((item) => item[key]))).sort((a, b) =>
    a.localeCompare(b)
  );

const formatDate = (value) => {
  if (!value) return "-";
  const [year, month, day] = value.split("-");
  return `${day}.${month}.${year}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const [date, time] = value.split(" ");
  return `${formatDate(date)} ${time || ""}`.trim();
};

const formatCoordinates = (device) =>
  `${device.lat.toFixed(5)}, ${device.lon.toFixed(5)}`;

const isFinderCell = (row, col) => {
  const inTopLeft = row < 7 && col < 7;
  const inTopRight = row < 7 && col > 19;
  const inBottomLeft = row > 19 && col < 7;

  if (!inTopLeft && !inTopRight && !inBottomLeft) return false;

  const localRow = row > 19 ? row - 20 : row;
  const localCol = col > 19 ? col - 20 : col;
  const border =
    localRow === 0 || localRow === 6 || localCol === 0 || localCol === 6;
  const center =
    localRow >= 2 && localRow <= 4 && localCol >= 2 && localCol <= 4;

  return border || center;
};

const shouldFillQrCell = (value, row, col) => {
  if (isFinderCell(row, col)) return true;

  const seed = value
    .split("")
    .reduce((sum, char, index) => sum + char.charCodeAt(0) * (index + 1), 0);

  return ((row * 31 + col * 17 + seed) % 7) < 3;
};

function QrPreview({ value }) {
  const cells = [];

  for (let row = 0; row < 27; row += 1) {
    for (let col = 0; col < 27; col += 1) {
      cells.push(
        <span
          className={`stt-gps-qr-cell ${
            shouldFillQrCell(value, row, col) ? "dark" : ""
          }`}
          key={`${row}-${col}`}
        />
      );
    }
  }

  return <div className='stt-gps-qr-grid'>{cells}</div>;
}

function StatCard({ label, value }) {
  return (
    <div className='stt-gps-stat-card'>
      <div className='stt-gps-stat-label'>{label}</div>
      <div className='stt-gps-stat-value'>{value}</div>
    </div>
  );
}

function DetailField({ label, value }) {
  return (
    <div className='stt-gps-field'>
      <span>{label}</span>
      <strong>{value || "-"}</strong>
    </div>
  );
}

function GoogleLocationEmbed({ device }) {
  if (!device) return null;

  return (
    <div className='stt-gps-location-embed'>
      <iframe
        allowFullScreen=''
        height='100%'
        loading='lazy'
        src={`https://maps.google.com/maps?q=${device.lat},${device.lon}&z=14&output=embed`}
        style={{ border: 0 }}
        title={`${device.deviceNumber} Google Map`}
        width='100%'
      />
    </div>
  );
}

function DeviceDetailContent({ device }) {
  if (!device) return null;

  return (
    <div className='stt-gps-modal-content'>
      <div className='stt-gps-detail-grid'>
        <div className='stt-gps-detail-block'>
          <h4>Qurilma ma'lumotlari</h4>
          <DetailField label='Qurilma raqami' value={device.deviceNumber} />
          <DetailField label='Turi' value={device.type} />
          <DetailField label='Ishlab chiqaruvchi' value={device.manufacturer} />
          <DetailField
            label='Integratsiya vaqti'
            value={formatDate(device.producedDate)}
          />
          <DetailField label='Yetkazilgan subyekt' value={device.suppliedSubject} />
          <DetailField label='STIR' value={device.stir} />
          <DetailField label='Viloyat' value={device.region} />
          <DetailField label='Tuman' value={device.district} />
          <DetailField label='Joylashuv' value={device.location} />
        </div>

        <div className='stt-gps-detail-block'>
          <h4>GPS monitoring</h4>
          <DetailField label='GPS Device ID' value={device.gpsId} />
          <DetailField label='Holati' value={device.gpsStatus} />
          <DetailField label='Latitude' value={device.lat.toFixed(5)} />
          <DetailField label='Longitude' value={device.lon.toFixed(5)} />
          <DetailField
            label="Oxirgi ma'lumot vaqti"
            value={formatDateTime(device.lastSignalAt)}
          />
          <DetailField label='Koordinata' value={formatCoordinates(device)} />
        </div>


      </div>

      <div className='stt-gps-modal-map-title'>Google kartadagi lokatsiya</div>
      <GoogleLocationEmbed device={device} />
    </div>
  );
}

function DeviceModal({ device, open, onClose }) {
  return (
    <Modal
      footer={null}
      onCancel={onClose}
      open={open}
      title="Qurilma haqida batafsil ma'lumot"
      width={980}>
      <DeviceDetailContent device={device} />
    </Modal>
  );
}

function SttGpsMap({ devices, onOpenDevice }) {
  return (
    <section className='stt-gps-google-card'>
      <div className='stt-gps-map-header'>
        <div>
          <div className='stt-gps-section-title'>GPS qurilmalar xaritasi</div>
          <p>
            Marker ustiga bosilganda qurilma ma'lumotlari modal oynada
            ko'rsatiladi.
          </p>
        </div>

        <div className='stt-gps-map-legend'>
          <span>
            <i className='active' /> GPS faol
          </span>
          <span>
            <i className='inactive' /> GPS nofaol
          </span>
        </div>
      </div>

      <MapContainer
        center={MAP_CENTER}
        className='stt-gps-google-map'
        maxBounds={[[36, 54], [47, 75]]}
        maxBoundsViscosity={0.8}
        minZoom={5}
        zoom={6}>
        <TileLayer
          url='https://tile.openstreetmap.org/{z}/{x}/{y}.png'
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          maxZoom={19}
        />
        <Polygon positions={UZBEKISTAN_MASK_PATHS} interactive={false}
          pathOptions={{ fillColor: "#050914", fillOpacity: 0.5, stroke: false }} />
        <Polygon positions={UZBEKISTAN_BOUNDARY} interactive={false}
          pathOptions={{ color: "#ffd45a", weight: 2.8, fillOpacity: 0 }} />
        {devices.map((device) => (
          <CircleMarker key={device.id}
            center={[device.lat, device.lon]} radius={8}
            pathOptions={{ color: "#ffffff", weight: 2, fillOpacity: 1,
              fillColor: device.gpsStatus === "Faol" ? "#2fb344" : "#e7515a" }}
            eventHandlers={{ click: () => onOpenDevice(device) }}>
            <Tooltip>{device.deviceNumber} — {device.gpsStatus}</Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </section>
  );
}

function SttGpsSahifa() {
  const [activeTab, setActiveTab] = useState(1);
  const [filters, setFilters] = useState({
    search: "",
    date: "",
    type: "all",
    gpsStatus: "all",
    region: "all",
    district: "all",
  });
  const [page, setPage] = useState(1);
  const [modalDevice, setModalDevice] = useState(null);

  const stats = useMemo(
    () => ({
      total: sttGpsDevices.length,
      pumps: sttGpsDevices.filter((device) => device.type === "Nasos").length,
      filters: sttGpsDevices.filter((device) => device.type === "Filtr").length,
      activeGps: sttGpsDevices.filter((device) => device.gpsStatus === "Faol")
        .length,
      inactiveGps: sttGpsDevices.filter(
        (device) => device.gpsStatus === "Nofaol"
      ).length,
    }),
    []
  );

  const regions = useMemo(() => uniqueValues(sttGpsDevices, "region"), []);

  const districts = useMemo(() => {
    const source =
      filters.region === "all"
        ? sttGpsDevices
        : sttGpsDevices.filter((device) => device.region === filters.region);

    return uniqueValues(source, "district");
  }, [filters.region]);

  const filteredDevices = useMemo(() => {
    const search = filters.search.trim().toLowerCase();

    return sttGpsDevices.filter((device) => {
      const matchesType = filters.type === "all" || device.type === filters.type;
      const matchesGps =
        filters.gpsStatus === "all" || device.gpsStatus === filters.gpsStatus;
      const matchesRegion =
        filters.region === "all" || device.region === filters.region;
      const matchesDistrict =
        filters.district === "all" || device.district === filters.district;
      const matchesDate =
        !filters.date || device.lastSignalAt.slice(0, 10) === filters.date;
      const searchSource = [
        device.deviceNumber,
        device.type,
        device.manufacturer,
        device.suppliedSubject,
        device.stir,
        device.qrId,
        device.gpsId,
        device.gpsStatus,
        device.region,
        device.district,
        device.location,
      ]
        .join(" ")
        .toLowerCase();

      return (
        matchesType &&
        matchesGps &&
        matchesRegion &&
        matchesDistrict &&
        matchesDate &&
        (!search || searchSource.includes(search))
      );
    });
  }, [filters]);

  const totalPages = Math.max(1, Math.ceil(filteredDevices.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleDevices = filteredDevices.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const changeFilter = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      ...(key === "region" ? { district: "all" } : {}),
    }));
  };

  const openDeviceModal = (device) => {
    setModalDevice(device);
  };

  return (
    <div className='stt-gps-page'>
      <ContentTop>
        <ul className='content-top-left stt-gps-top'>
          <li className='stt-gps-title'>
            STT va GPS
            <span className='stt-gps-subtitle'>
              QR-kod reyestri va GPS monitoring demo sahifasi
            </span>
          </li>
          <div>
            <Tabs tabs={tabs} toggle={activeTab} setToggle={setActiveTab} />
          </div>
        </ul>
      </ContentTop>

      {activeTab === 1 ? (
        <>
          <section className='stt-gps-stats'>
            <StatCard label='Jami qurilmalar' value={stats.total} />
            <StatCard label='Nasoslar' value={stats.pumps} />
            <StatCard label='Filtrlar' value={stats.filters} />
            <StatCard label='GPS faol' value={stats.activeGps} />
            <StatCard label='GPS nofaol' value={stats.inactiveGps} />
          </section>

          <section className='table-box stt-gps-registry'>
            <div className='table-content-top stt-gps-table-top'>
              <div className='stt-gps-search-row'>
                <Input
                  prefix={<SearchOutlined className='site-form-item-icon' />}
                  placeholder='Qidiruv'
                  value={filters.search}
                  onChange={(event) => changeFilter("search", event.target.value)}
                />
              </div>

              <div className='stt-gps-filters'>
                <input
                  className='form-control form-control-sm stt-gps-filter'
                  type='date'
                  value={filters.date}
                  onChange={(event) => changeFilter("date", event.target.value)}
                />

                <select
                  className='form-select form-control-sm stt-gps-filter'
                  value={filters.type}
                  onChange={(event) => changeFilter("type", event.target.value)}>
                  <option value='all'>Barcha turlar</option>
                  <option value='Nasos'>Nasos</option>
                  <option value='Filtr'>Filtr</option>
                </select>

                <select
                  className='form-select form-control-sm stt-gps-filter'
                  value={filters.gpsStatus}
                  onChange={(event) =>
                    changeFilter("gpsStatus", event.target.value)
                  }>
                  <option value='all'>Barcha GPS holati</option>
                  <option value='Faol'>GPS faol</option>
                  <option value='Nofaol'>GPS nofaol</option>
                </select>

                <select
                  className='form-select form-control-sm stt-gps-filter'
                  value={filters.region}
                  onChange={(event) => changeFilter("region", event.target.value)}>
                  <option value='all'>Barcha viloyatlar</option>
                  {regions.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>

                <select
                  className='form-select form-control-sm stt-gps-filter'
                  value={filters.district}
                  onChange={(event) =>
                    changeFilter("district", event.target.value)
                  }>
                  <option value='all'>Barcha tumanlar</option>
                  {districts.map((district) => (
                    <option key={district} value={district}>
                      {district}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className='table-responsive stt-gps-table-wrap'>
              <table className='table table-bordered table-style stt-gps-table'>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Qurilma raqami</th>
                    <th>Turi</th>
                    <th>Ishlab chiqaruvchi</th>
                    <th>Integratsiya vaqti</th>
                    <th>Yetkazilgan subyekt</th>
                    <th>STIR</th>
                    <th>QR-kod</th>
                    <th>GPS ID</th>
                    <th>GPS holati</th>
                    <th>Oxirgi ma'lumot vaqti</th>
                    <th>Ko'rish</th>
                  </tr>
                </thead>

                <tbody>
                  {visibleDevices.length ? (
                    visibleDevices.map((device) => (
                      <tr key={device.id}>
                        <td>{device.id}</td>
                        <td>{device.deviceNumber}</td>
                        <td>
                          <span className='stt-gps-type-badge'>
                            {device.type}
                          </span>
                        </td>
                        <td>{device.manufacturer}</td>
                        <td>{formatDate(device.producedDate)}</td>
                        <td className='text-start'>{device.suppliedSubject}</td>
                        <td>{device.stir}</td>
                        <td>
                          <span className='stt-gps-qr-badge'>{device.qrId}</span>
                        </td>
                        <td>{device.gpsId}</td>
                        <td>
                          <span
                            className={`stt-gps-status-badge ${getStatusClass(
                              device.gpsStatus
                            )}`}>
                            {device.gpsStatus}
                          </span>
                        </td>
                        <td>{formatDateTime(device.lastSignalAt)}</td>
                        <td>
                          <button
                            className='stt-gps-view-button'
                            onClick={() => openDeviceModal(device)}
                            title="Ko'rish"
                            type='button'>
                            <EyeOutlined />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className='stt-gps-empty' colSpan={12}>
                        Filtr bo'yicha ma'lumot topilmadi.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className='stt-gps-pagination'>
              <div className='stt-gps-pagination-info'>
                Showing{" "}
                {(currentPage - 1) * PAGE_SIZE + (filteredDevices.length ? 1 : 0)}
                -{Math.min(currentPage * PAGE_SIZE, filteredDevices.length)} of{" "}
                {filteredDevices.length} items.
              </div>

              <div className='stt-gps-pagination-actions'>
                <button
                  className='stt-gps-page-button'
                  disabled={currentPage === 1}
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                  type='button'>
                  Prev
                </button>

                {Array.from({ length: totalPages }).map((_, index) => {
                  const pageNumber = index + 1;

                  return (
                    <button
                      className={`stt-gps-page-button ${
                        currentPage === pageNumber ? "active" : ""
                      }`}
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      type='button'>
                      {pageNumber}
                    </button>
                  );
                })}

                <button
                  className='stt-gps-page-button'
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setPage((value) => Math.min(totalPages, value + 1))
                  }
                  type='button'>
                  Next
                </button>
              </div>
            </div>
          </section>
        </>
      ) : (
        <SttGpsMap devices={sttGpsDevices} onOpenDevice={openDeviceModal} />
      )}

      <DeviceModal
        device={modalDevice}
        onClose={() => setModalDevice(null)}
        open={Boolean(modalDevice)}
      />
    </div>
  );
}

export default SttGpsSahifa;
